import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { useAuth } from "./useAuth";
import { useRegisterPush, useUnregisterPush } from "./usePushRegistration";
import { pushAndroidChannel } from "./pushAndroidChannel";

const LOCAL_STORAGE_FCM_KEY = "bhadapms_fcm_token";
const LOCAL_STORAGE_OPTOUT_KEY = "bhadapms_push_optout";
const FirebaseMessagingFallback = registerPlugin<any>("FirebaseMessaging");

let diagnosticLogged = false;

export function useNativePush() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const { mutateAsync: registerPush } = useRegisterPush();
  const { mutateAsync: unregisterPush } = useUnregisterPush();
  
  const [pushEnabled, setPushEnabled] = useState(() => {
    if (typeof window !== "undefined") {
      const existingToken = localStorage.getItem(LOCAL_STORAGE_FCM_KEY);
      const isOptedOut = localStorage.getItem(LOCAL_STORAGE_OPTOUT_KEY) === "true";
      return !!existingToken && !isOptedOut;
    }
    return false;
  });
  const listenerRef = useRef<any>(null);

  const getFirebaseMessaging = () => {
    return (globalThis as any).Capacitor?.Plugins?.FirebaseMessaging || FirebaseMessagingFallback;
  };

  const setupChannel = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;
    const FirebaseMessaging = getFirebaseMessaging();
    if (!FirebaseMessaging) return;

    try {
      if (typeof FirebaseMessaging.createChannel === "function") {
        await FirebaseMessaging.createChannel(pushAndroidChannel);
      }
    } catch (e) {
      console.warn("Failed to create Android Notification Channel", e);
    }
  }, []);

  const enablePush = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;
    const FirebaseMessaging = getFirebaseMessaging();
    if (!FirebaseMessaging) return;

    try {
      localStorage.removeItem(LOCAL_STORAGE_OPTOUT_KEY);

      // 1. Request Permission
      let perm = await FirebaseMessaging.checkPermissions();
      if (perm.receive !== "granted") {
        perm = await FirebaseMessaging.requestPermissions();
      }
      
      if (perm.receive !== "granted") {
        setPushEnabled(false);
        return;
      }

      // 2. Fetch Token
      const response = await FirebaseMessaging.getToken();
      const fcmToken = response.token;
      
      if (!fcmToken) {
        setPushEnabled(false);
        return;
      }

      // 3. Register against our backend
      const previousIdentity = localStorage.getItem(LOCAL_STORAGE_FCM_KEY) || undefined;
      const platform = Capacitor.getPlatform() === "ios" ? "ios" : "android";

      const res = await registerPush({ 
        fcmToken, 
        platform,
        previousIdentity 
      });

      if (res.success) {
        localStorage.setItem(LOCAL_STORAGE_FCM_KEY, fcmToken);
        setPushEnabled(true);
      }

    } catch (e) {
      console.error("Failed to enable native push:", e);
      setPushEnabled(false);
    }
  }, [registerPush]);

  const disablePush = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;
    const FirebaseMessaging = getFirebaseMessaging();
    
    try {
      localStorage.setItem(LOCAL_STORAGE_OPTOUT_KEY, "true");
      
      const existingToken = localStorage.getItem(LOCAL_STORAGE_FCM_KEY);
      if (existingToken) {
        await unregisterPush({ identity: existingToken });
        localStorage.removeItem(LOCAL_STORAGE_FCM_KEY);
      }

      if (FirebaseMessaging && typeof FirebaseMessaging.deleteToken === "function") {
        await FirebaseMessaging.deleteToken();
      }
      setPushEnabled(false);
    } catch (e) {
      console.error("Failed to disable native push:", e);
    }
  }, [unregisterPush]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || authState.type !== "authenticated") return;

    let isCancelled = false;
    const FirebaseMessaging = getFirebaseMessaging();
    
    if (FirebaseMessaging) {
      // Always ensure the channel exists for Android payload headers compatibility
      setupChannel();
      
      // Silent sync flow to capture if the system rotated our token under the hood
      // Only run if the user hasn't explicitly opted out and we already have a token
      const existingToken = localStorage.getItem(LOCAL_STORAGE_FCM_KEY);
      const isOptedOut = localStorage.getItem(LOCAL_STORAGE_OPTOUT_KEY) === "true";
      if (existingToken && !isOptedOut) {
        (async () => {
          try {
            const perm = await FirebaseMessaging.checkPermissions();
            if (perm.receive === "granted") {
              const response = await FirebaseMessaging.getToken();
              const fcmToken = response.token;
              if (fcmToken) {
                const platform = Capacitor.getPlatform() === "ios" ? "ios" : "android";
                const previousIdentity = existingToken !== fcmToken ? existingToken : undefined;
                const regRes = await registerPush({ fcmToken, platform, previousIdentity });
                if (regRes.success) {
                  localStorage.setItem(LOCAL_STORAGE_FCM_KEY, fcmToken);
                  setPushEnabled(true);
                }
              }
            }
          } catch (e) {
            console.warn("Silent push sync failed", e);
          }
        })();
      }

      // Listen for notification taps inside the app
      FirebaseMessaging.addListener("notificationActionPerformed", (event: any) => {
        if (isCancelled) return;
        
        const data = event.notification?.data;
        if (data) {
          const tapUrl = data.url || data.link;
          if (tapUrl && typeof tapUrl === "string") {
            navigate(tapUrl);
          }
        }
      }).then((handle: any) => {
        if (isCancelled && handle?.remove) {
          handle.remove();
        } else {
          listenerRef.current = handle;
        }
      }).catch((e: any) => console.warn("Failed to listen for notification interaction", e));
    }

    return () => {
      isCancelled = true;
      if (listenerRef.current && listenerRef.current.remove) {
        listenerRef.current.remove();
      }
    };
  }, [authState.type, registerPush, setupChannel, navigate]);

  return { pushEnabled, enablePush, disablePush };
}